import { Logger } from 'vue-infra';
import { uniqBy } from 'lodash';
import { INFRA_GETTERS, WIDGET_PATHS } from '@core/store';
import TranscriptService, { EVENTS } from '@core/services/transcript/TranscriptService';
import { umsClient } from '@core/clients/ums/UMSClient';
import { DATA_LOADING_STATES, CONVERSATION_STATUSES, FEATURES } from '@config/Consts';
import { getServiceBaseURI } from '@core/utils/utils';
import * as TranscriptWidgetBam from '@core/bam/TranscriptWidgetBam';

const logger = Logger.getLogger('transcript');

async function initialise({ dispatch, rootState, rootGetters }) {
  const { session: { accountId, glob, userId, baseURIs } } = rootState;
  const domain = rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.SOCIAL_CONNECT) ?
    getServiceBaseURI('socialMsgDomain', baseURIs)
    : getServiceBaseURI('asyncMessagingEnt', baseURIs);
  const hoursSettings = rootGetters[`${WIDGET_PATHS.CONVERSATIONS}/getHourFormatSiteSetting`];
  const umsConfig = {
    accountId,
    agentToken: glob,
    agentId: `${accountId}.${userId}`,
    domain,
  };
  try {
    umsClient.init(umsConfig);
  } catch (error) {
    logger.error('couldn\'t initialise UMS client', 'transcript::actions::init', error);
    dispatch('handleError', {
      conversationId: 'n/a',
      errorObj: error,
      errorMessage: 'cannot initialise UMS connection',
      errorNamespace: 'initialise',
    });
    umsClient.init(umsConfig);
  } finally {
    TranscriptService.init();
    TranscriptService.setHoursSettings(hoursSettings);
  }
}

async function openTranscript({ commit, state }) {
  if (!state.isOpen) {
    commit('setProp', { prop: 'isOpen', value: true });
  }
}

async function closeTranscript({ commit, state, dispatch }) {
  if (state.isOpen) {
    commit('setProp', { prop: 'isOpen', value: false });
    dispatch('resetTranscript');
    dispatch('unsubscribeFromConversation');
  }
}

async function selectConversation({ commit, state, dispatch }, { conversation }) {
  const { conversationId } = conversation;
  if (!state.isOpen) {
    dispatch('openTranscript');
    TranscriptWidgetBam.sendOpenCloseBAM(true);
  }
  if (
    !state.conversation ||
    (state.conversation && state.conversation.conversationId !== conversationId)
  ) {
    commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.LOADING });
    commit('setProp', { prop: 'conversation', value: conversation });
    dispatch('loadTranscript', conversation);
  }
}

async function subscribeToConversation({ dispatch, rootState }, conversation) {
  const { conversationId, dialogId, consumerId } = conversation;
  await TranscriptService.subscribeToConversation({
    brandId: rootState.session.accountId,
    conversationId,
    dialogId,
    consumerId,
  });
  TranscriptService.on(EVENTS.ON_CONVERSATION_SUBSCRIBE_ERROR, (error) => {
    dispatch('handleError', {
      conversationId,
      errorObj: error,
      errorMessage: 'cannot subscribe to conversation',
      errorNamespace: 'subscribeToConversation',
      showErrorInTranscript: true,
    });
  }, 'transcriptSubscribeToConversation');
}


async function unsubscribeFromConversation({ state, dispatch }) {
  try {
    await TranscriptService.unsubscribeFromConversation();
  } catch (error) {
    dispatch('handleError', {
      showErrorInTranscript: false,
      conversationId: state.conversation.conversationId,
      errorObj: error,
      errorMessage: 'cannot unsubscribe from conversation',
      errorNamespace: 'unsubscribeFromConversation',
    });
  }
}

async function loadTranscript({ dispatch }, conversation) {
  if (conversation.status === CONVERSATION_STATUSES.CLOSE) {
    dispatch('getHistoryTranscript', conversation);
  } else {
    await dispatch('subscribeToConversation', conversation);
    // we need to wait to get all the participants details
    // then load messages and produce Transcript :(
    TranscriptService.on(
      EVENTS.ON_PARTICIPANTS_READY,
      () => dispatch('getUMSTranscript', conversation),
      'transcriptParticipantsReady',
    );
    TranscriptService.on(
      EVENTS.ON_MESSAGE_RECEIVED,
      message => dispatch('handleIncomingMessages', message),
      'transcriptMessageReceived',
    );
  }
}

async function getHistoryTranscript({ commit, dispatch }, params) {
  const { brandName, conversationId, consumerId } = params;
  commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.LOADING });
  try {
    const transcript = await TranscriptService.getHistoryTranscript({
      brandName,
      conversationId,
      consumerId,
    });
    commit('setProp', { prop: 'transcript', value: transcript });
    commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.SUCCESS });
  } catch (error) {
    dispatch('handleError', {
      conversationId,
      errorObj: error,
      errorMessage: 'cannot get history transcript',
      errorNamespace: 'getHistoryTranscript',
      showErrorInTranscript: true,
    });
  }
}

async function getUMSTranscript({ commit, dispatch }, conversation) {
  commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.LOADING });
  const { conversationId, dialogs } = conversation;
  const dialogId = dialogs && dialogs[0] && dialogs[0].dialogId;
  if (dialogId) {
    try {
      const transcript = await TranscriptService.getConversationTranscript({
        conversationId,
        dialogId,
      });
      commit('setProp', { prop: 'transcript', value: transcript });
      commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.SUCCESS });
    } catch (error) {
      dispatch('handleError', {
        conversationId,
        errorObj: error,
        errorMessage: 'cannot get UMS transcript',
        errorNamespace: 'getUMSTranscript',
        showErrorInTranscript: true,
      });
    }
  } else {
    dispatch('handleError', {
      conversationId,
      errorMessage: 'cannot get transcript - no dialogId found',
      errorNamespace: 'getUMSTranscript',
      showErrorInTranscript: true,
    });
  }
}

async function handleIncomingMessages({ state, commit }, message) {
  if (state.transcript.messages) {
    const messages = uniqBy([...state.transcript.messages, message], 'id');
    const transcript = state.transcript;
    transcript.messages = messages;
    commit('setProp', { prop: 'transcript', value: transcript });
  }
}

async function resetTranscript({ commit }) {
  commit('resetState');
  TranscriptService.clearAllListeners(EVENTS.ON_PARTICIPANTS_READY);
  TranscriptService.clearAllListeners(EVENTS.ON_MESSAGE_RECEIVED);
  TranscriptService.clearAllListeners(EVENTS.ON_CONVERSATION_SUBSCRIBE_ERROR);
}

/**
 * Error handling action
 * @param commit - Vuex commit
 * @param params
 * @param params.conversationId {string}
 * @param params.errorObj - error object caught
 * @param params.errorMessage {string} - error message to be logged/BAMed
 * @param params.errorNamespace {string} - error namespace (function name is called in)
 * @return {Promise<void>}
 */
async function handleError({ commit }, params) {
  const {
    conversationId,
    errorMessage,
    errorNamespace,
    errorObj = {},
    showErrorInTranscript = true,
  } = params;
  if (showErrorInTranscript) {
    commit('setProp', { prop: 'dataStatus', value: DATA_LOADING_STATES.ERROR });
  }
  const messageToLog = `${errorMessage}; ERROR :: ${JSON.stringify(errorObj)}`;
  logger.error(messageToLog, `transcript::actions::${errorNamespace}`, errorObj);
  TranscriptWidgetBam.sendErrorBAM(conversationId, messageToLog);
}

export default {
  initialise,
  openTranscript,
  closeTranscript,
  selectConversation,
  subscribeToConversation,
  unsubscribeFromConversation,
  loadTranscript,
  getHistoryTranscript,
  getUMSTranscript,
  resetTranscript,
  handleIncomingMessages,
  handleError,
};
